import {
  PortalLoginSchema,
  PortalProcedureListSchema,
  PortalSessionSchema,
  type PortalLoginInput,
  type PortalProcedureList,
  type PortalSession,
} from '@datazo/contracts';

/**
 * El portal del paciente usa su propio cliente HTTP — no comparte cookie ni
 * Bearer con el staff. El token vive en sessionStorage para que cerrar la
 * pestaña termine la sesión (decisión simple y conservadora para portal
 * público).
 */
const PORTAL_TOKEN_KEY = 'datazo.portal.token';
const PORTAL_PATIENT_KEY = 'datazo.portal.patient';

export function readPortalToken(): string | null {
  return sessionStorage.getItem(PORTAL_TOKEN_KEY);
}

export function readPortalPatient(): PortalSession['paciente'] | null {
  const raw = sessionStorage.getItem(PORTAL_PATIENT_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as PortalSession['paciente'];
  } catch {
    return null;
  }
}

export function clearPortalSession(): void {
  sessionStorage.removeItem(PORTAL_TOKEN_KEY);
  sessionStorage.removeItem(PORTAL_PATIENT_KEY);
}

function persistSession(session: PortalSession): void {
  sessionStorage.setItem(PORTAL_TOKEN_KEY, session.token);
  sessionStorage.setItem(PORTAL_PATIENT_KEY, JSON.stringify(session.paciente));
}

async function call<T>(
  path: string,
  init: RequestInit,
  parser: { parse(value: unknown): T },
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = (await res.json()) as { message?: string; title?: string };
      detail = body.message ?? body.title ?? detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const raw = (await res.json()) as unknown;
  return parser.parse(raw);
}

export async function portalLogin(input: PortalLoginInput): Promise<PortalSession> {
  const body = PortalLoginSchema.parse(input);
  const session = await call<PortalSession>(
    '/api/portal/login',
    { method: 'POST', body: JSON.stringify(body) },
    PortalSessionSchema,
  );
  persistSession(session);
  return session;
}

export async function fetchMyProcedures(): Promise<PortalProcedureList> {
  const token = readPortalToken();
  if (token === null) throw new Error('Sesión expirada — vuelve a iniciar sesión');
  return call<PortalProcedureList>(
    '/api/portal/me/procedures',
    { method: 'GET', headers: { Authorization: `Bearer ${token}` } },
    PortalProcedureListSchema,
  );
}

/** URL pública para abrir/descargar un resultado desde el portal. */
export function portalDownloadUrl(resultId: string): string | null {
  const token = readPortalToken();
  if (token === null) return null;
  // El backend lee el Bearer; para abrir en nueva pestaña necesitamos un
  // método compatible. Aquí devolvemos solo el path; el caller arma un fetch
  // y abre un blob URL (ver PortalResultsPage).
  return `/api/portal/results/${encodeURIComponent(resultId)}/download`;
}
